import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Table,
  Input,
  Label,
  Button,
  Collapse,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";
import dayjs from "dayjs";
import {
  useGetBookingDetails,
  useApproveBooking,
  useRemoveWorkers,
  useGenerateInvoice
} from "../../api/booking";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Spinners from "../../components/Common/Spinner";
import toastr from "toastr";
import WorkerAssignmentPanel from "./worker-assignment/WorkerAssignmentPanel";

const BookingReview = () => {
  const { bookingSlug } = useParams();
  const navigate = useNavigate();
  const { data: bookingData, isLoading, error, refetch } = useGetBookingDetails(bookingSlug);
  const approveBooking = useApproveBooking();
  const removeWorkers = useRemoveWorkers();
  const generateInvoice = useGenerateInvoice();

  // Accordion state for shift patterns
  const [openPatterns, setOpenPatterns] = useState({});

  // Date details modal state
  const [dateDetailsModal, setDateDetailsModal] = useState(false);
  const [selectedDateData, setSelectedDateData] = useState(null);
  const [invoiceAmount, setInvoiceAmount] = useState("");

  // Approval form state
  const [approvalForm, setApprovalForm] = useState({
    NumberToMultipleWorker: "",
    perShiftPrice: []
  });

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({
    NumberToMultipleWorker: ""
  });

  const booking = bookingData?.data ?? bookingData;

  const initialAmountGbp = booking?.actualInitialAmount;

  // Initialize form when booking data loads
  useEffect(() => {
    if (booking?.shiftPattern) {
      const initialPerShiftPrice = booking.shiftPattern.map((pattern, index) => {
        const priceObj = {
          shiftIndex: index,
          weekdayPrice: pattern.weekdayPrice || ""
        };
        // Only include optional prices if they exist in the pattern
        if (pattern.weekendPrice !== undefined && pattern.weekendPrice !== null) {
          priceObj.weekendPrice = pattern.weekendPrice;
        }
        if (pattern.nightPrice !== undefined && pattern.nightPrice !== null) {
          priceObj.nightPrice = pattern.nightPrice;
        }
        if (pattern.urgentCoverPrice !== undefined && pattern.urgentCoverPrice !== null) {
          priceObj.urgentCoverPrice = pattern.urgentCoverPrice;
        }
        if (pattern.bankHolidayPrice !== undefined && pattern.bankHolidayPrice !== null) {
          priceObj.bankHolidayPrice = pattern.bankHolidayPrice;
        }
        return priceObj;
      });
      setApprovalForm({
        NumberToMultipleWorker: "",
        perShiftPrice: initialPerShiftPrice
      });
    }
  }, [booking]);

  useEffect(() => {
    if (booking) {
      const initial = booking.actualInitialAmount ?? "";
      const amount = booking.finalAmount ?? initial ?? "";
      setInvoiceAmount(amount !== "" && amount != null ? String(amount) : "");
    }
  }, [booking]);

  // Validate workers per date against quantity
  useEffect(() => {
    if (booking?.shiftPattern) {
      const validationIssues = [];
      const shiftsToFix = [];

      booking.shiftPattern.forEach((pattern, patternIndex) => {
        const quantity = pattern.quantity || 0;
        if (pattern.dates && pattern.dates.length > 0) {
          pattern.dates.forEach((dateItem) => {
            const workersCount = dateItem.workers ? dateItem.workers.length : 0;
            if (workersCount > quantity) {
              validationIssues.push({
                patternIndex,
                date: dateItem.date,
                quantity,
                workersCount,
                excess: workersCount - quantity
              });
              // Prepare data to remove excess workers
              const excessWorkers = dateItem.workers.slice(quantity);
              if (excessWorkers.length > 0) {
                if (!shiftsToFix[patternIndex]) {
                  shiftsToFix[patternIndex] = {
                    shiftIndex: patternIndex,
                    changes: []
                  };
                }
                excessWorkers.forEach((worker) => {
                  const workerSlug = worker.user?.slug || worker.workerId;
                  if (workerSlug) {
                    const existingChange = shiftsToFix[patternIndex].changes.find(
                      (c) => c.workerSlug === workerSlug
                    );
                    if (existingChange) {
                      if (!existingChange.dates.includes(dateItem.date)) {
                        existingChange.dates.push(dateItem.date);
                      }
                    } else {
                      shiftsToFix[patternIndex].changes.push({
                        workerSlug,
                        dates: [dateItem.date]
                      });
                    }
                  }
                });
              }
            }
          });
        }
      });

      if (validationIssues.length > 0) {
        const issueMessages = validationIssues.map(
          (issue) =>
            `Shift Pattern ${issue.patternIndex + 1}, Date ${dayjs(issue.date).format(
              "DD MMM YYYY"
            )}: ${issue.excess} excess worker(s) (${issue.workersCount} workers, ${
              issue.quantity
            } required)`
        );
        toastr.warning(
          `Validation Issue: Some dates have more workers than required. ${issueMessages.join(
            "; "
          )}`
        );

        // Automatically remove excess workers if there are any
        const shiftsToRemove = Object.values(shiftsToFix).filter(
          (shift) => shift.changes.length > 0
        );
        if (shiftsToRemove.length > 0) {
          removeWorkers.mutate(
            { bookingSlug, payload: { shifts: shiftsToRemove } },
            {
              onSuccess: () => {
                toastr.success("Excess workers removed automatically");
                refetch();
              },
              onError: () => {
                toastr.error("Failed to remove excess workers. Please remove them manually.");
              }
            }
          );
        }
      }
    }
  }, [booking, bookingSlug, removeWorkers, refetch]);

  const canGenerateInvoice = () => {
    if (!booking) return false;
    if (booking.requestInvoice !== true) return false;
    if (booking.finalInvoice != null) return false;
    if (booking.finalAmount != null && booking.finalAmount !== "") return false;
    return true;
  };

  const handleGenerateInvoice = () => {
    const parsedAmount = parseFloat(invoiceAmount);
    if (!parsedAmount || parsedAmount <= 0) {
      toastr.error("Please enter a valid final amount");
      return;
    }

    generateInvoice.mutate(
      {
        bookingSlug,
        payload: {
          finalAmount: String(parsedAmount)
        }
      },
      {
        onSuccess: () => {
          toastr.success("Invoice generated successfully!");
          refetch();
        },
        onError: () => {
          // Error toast is handled by global axios interceptor
        }
      }
    );
  };

  const togglePattern = (index) => {
    setOpenPatterns((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handlePriceChange = (shiftIndex, field, value) => {
    setApprovalForm((prev) => {
      const newPerShiftPrice = [...prev.perShiftPrice];
      const shiftPriceIndex = newPerShiftPrice.findIndex((sp) => sp.shiftIndex === shiftIndex);
      if (shiftPriceIndex >= 0) {
        newPerShiftPrice[shiftPriceIndex] = {
          ...newPerShiftPrice[shiftPriceIndex],
          [field]: value ? parseFloat(value) : ""
        };
      }
      return {
        ...prev,
        perShiftPrice: newPerShiftPrice
      };
    });
  };

  const handleNumberToMultipleWorkerChange = (value) => {
    setApprovalForm((prev) => ({
      ...prev,
      NumberToMultipleWorker: value ? parseInt(value) : ""
    }));
    // Clear validation error when user types
    setValidationErrors((prev) => {
      if (prev.NumberToMultipleWorker) {
        return {
          ...prev,
          NumberToMultipleWorker: ""
        };
      }
      return prev;
    });
  };

  const handleApprove = () => {
    // Validate form
    let hasErrors = false;
    const errors = { NumberToMultipleWorker: "" };

    if (!approvalForm.NumberToMultipleWorker || approvalForm.NumberToMultipleWorker <= 0) {
      errors.NumberToMultipleWorker =
        "Number to Multiple Worker is required and must be greater than 0";
      hasErrors = true;
    }

    if (hasErrors) {
      setValidationErrors(errors);
      toastr.error("Please fix the validation errors");
      return;
    }

    // Clear validation errors if validation passes
    setValidationErrors({ NumberToMultipleWorker: "" });

    const hasInvalidPrices = approvalForm.perShiftPrice.some(
      (sp) => !sp.weekdayPrice || sp.weekdayPrice <= 0
    );

    if (hasInvalidPrices) {
      toastr.error("Please enter valid weekday prices for all shift patterns");
      return;
    }

    const payload = {
      status: true,
      NumberToMultipleWorker: approvalForm.NumberToMultipleWorker,
      perShiftPrice: approvalForm.perShiftPrice.map((sp) => {
        const priceObj = {
          shiftIndex: sp.shiftIndex,
          weekdayPrice: sp.weekdayPrice
        };
        if (sp.weekendPrice) priceObj.weekendPrice = sp.weekendPrice;
        if (sp.nightPrice) priceObj.nightPrice = sp.nightPrice;
        if (sp.urgentCoverPrice) priceObj.urgentCoverPrice = sp.urgentCoverPrice;
        if (sp.bankHolidayPrice) priceObj.bankHolidayPrice = sp.bankHolidayPrice;
        return priceObj;
      })
    };

    approveBooking.mutate(
      { bookingSlug, payload },
      {
        onSuccess: () => {
          toastr.success("Booking approved successfully!");
          refetch();
        },
        onError: () => {
          // Error toast is handled by global axios interceptor
        }
      }
    );
  };

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Booking Details" />
          <Spinners setLoading={() => {}} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Booking Details" />
          <div className="alert alert-danger">
            Error loading booking details: {error.message || "Unknown error"}
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Booking Details" />
          <div className="text-center py-5">
            <div className="text-muted">
              <i className="mdi mdi-information-outline" style={{ fontSize: "48px" }}></i>
              <p className="mt-3">Booking not found.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const v = String(status ?? "").toLowerCase();
    switch (v) {
      case "pending":
        return <Badge className="bg-warning text-dark">Pending</Badge>;
      case "processing":
        return <Badge className="bg-info">Processing</Badge>;
      case "completed":
        return <Badge className="bg-success">Completed</Badge>;
      case "cancelled":
        return <Badge className="bg-danger">Cancelled</Badge>;
      default:
        return <Badge className="bg-secondary">{status || "-"}</Badge>;
    }
  };

  const getBookingTypeBadge = (type) => {
    const v = String(type ?? "").toLowerCase();
    switch (v) {
      case "urgent":
        return <Badge className="bg-danger">Urgent</Badge>;
      case "not_urgent":
        return <Badge className="bg-primary">Not Urgent</Badge>;
      default:
        return <Badge className="bg-secondary">{type || "-"}</Badge>;
    }
  };

  // Check if shift pattern has specific price types (from booking API response)
  const hasPriceType = (pattern, type) => {
    if (!pattern) return false;
    // Check if the field exists and is not null/undefined
    return pattern[type] !== undefined && pattern[type] !== null;
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Bookings" breadcrumbItem="Booking Details" />

        {/* Booking Overview */}
        <Card className="mb-3">
          <CardHeader className="bg-primary text-white">
            <h5 className="mb-0">Booking Overview</h5>
          </CardHeader>
          <CardBody>
            <Row className="gy-3">
              <Col md={3}>
                <small className="text-muted d-block">Booking ID</small>
                <div className="fw-medium">{booking.slug || "-"}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Status</small>
                <div>{getStatusBadge(booking.status)}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Booking Type</small>
                <div>{getBookingTypeBadge(booking.bookingType)}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Created Date</small>
                <div>
                  {booking.createdAt
                    ? dayjs(booking.createdAt).format("DD MMM YYYY, HH:mm")
                    : "-"}
                </div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Client</small>
                <div className="fw-medium">
                  {booking.createdBy?.fullName || "-"}
                </div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Client Email</small>
                <div>{booking.createdBy?.email || "-"}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">PO Required</small>
                <div>
                  {booking.hasPORequired ? (
                    <Badge className="bg-success">Yes</Badge>
                  ) : (
                    <Badge className="bg-secondary">No</Badge>
                  )}
                </div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">PO Reference</small>
                <div>{booking.poReference || "-"}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Net payment terms</small>
                <div>
                  {booking.netPaymentTerms != null
                    ? `${booking.netPaymentTerms} days`
                    : "-"}
                </div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Invoice requested</small>
                <div>
                  {booking.requestInvoice ? (
                    <Badge className="bg-success">Yes</Badge>
                  ) : (
                    <Badge className="bg-secondary">No</Badge>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Location Information */}
        <Card className="mb-3">
          <CardHeader className="bg-primary text-white">
            <h5 className="mb-0">Location Information</h5>
          </CardHeader>
          <CardBody>
            <Row className="gy-2">
              <Col md={6}>
                <small className="text-muted d-block">Site Name</small>
                <div className="fw-medium">
                  {booking.location?.siteName || "-"}
                </div>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">Search Address</small>
                <div>{booking.location?.searchAddress || "-"}</div>
              </Col>
              <Col md={12}>
                <small className="text-muted d-block">Full Address</small>
                <div>{booking.location?.fullAddress || "-"}</div>
              </Col>
              {booking.location?.latitude != null &&
                booking.location?.longitude != null && (
                <Col md={12}>
                  <small className="text-muted d-block">Coordinates</small>
                  <div>
                    {booking.location.latitude}, {booking.location.longitude}
                  </div>
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>

        {/* Pricing Information */}
        <Card className="mb-3">
          <CardHeader className="bg-primary text-white">
            <h5 className="mb-0">Pricing Information</h5>
          </CardHeader>
          <CardBody>
            <Row className="g-3">
              <Col md={4}>
                <div className="text-center p-3 border rounded">
                  <small className="text-muted d-block mb-2">
                    Initial Amount
                  </small>
                  <h5 className="mb-0 text-primary">
                    £
                    {parseFloat(initialAmountGbp || 0).toFixed(2)}
                  </h5>
                </div>
              </Col>
              {booking.discountType &&
                parseFloat(booking.discountedAmount || 0) > 0 && (
                  <Col md={4}>
                    <div className="text-center p-3 border rounded">
                      <small className="text-muted d-block mb-2">
                        Discount (
                        {booking.discountType === "percentage"
                          ? `${booking.discountedAmount}%`
                          : `£${booking.discountedAmount}`}
                        )
                      </small>
                      <h5 className="mb-0 text-success">
                        {booking.discountType === "percentage"
                          ? `${booking.discountedAmount}%`
                          : `£${parseFloat(booking.discountedAmount).toFixed(
                              2
                            )}`}
                      </h5>
                    </div>
                  </Col>
                )}
              <Col md={4}>
                <div className="text-center p-3 border rounded bg-primary text-white">
                  <small className="d-block opacity-75 mb-2">
                    Final Amount
                  </small>
                  <h5 className="mb-0">
                    £
                    {parseFloat(
                      booking.finalAmount ?? initialAmountGbp ?? 0
                    ).toFixed(2)}
                  </h5>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Notes */}
        {booking.notes && (
          <Card className="mb-3">
            <CardHeader className="bg-primary text-white">
              <h5 className="mb-0">Notes</h5>
            </CardHeader>
            <CardBody>
              <p className="mb-0">{booking.notes}</p>
            </CardBody>
          </Card>
        )}

        {/* Booking Approval Form */}
        {booking.status === "pending" && (
          <Card className="mb-3 border-warning">
            <CardHeader className="bg-warning text-dark">
              <h5 className="mb-0">Approve Booking</h5>
            </CardHeader>
            <CardBody>
              <Row className="gy-3">
                <Col md={12}>
                  <Label for="numberToMultipleWorker">
                    Number to Multiple Worker{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="number"
                    id="numberToMultipleWorker"
                    min="1"
                    value={approvalForm.NumberToMultipleWorker}
                    onChange={(e) =>
                      handleNumberToMultipleWorkerChange(e.target.value)
                    }
                    placeholder="Enter number of workers"
                    invalid={!!validationErrors.NumberToMultipleWorker}
                  />
                  {validationErrors.NumberToMultipleWorker && (
                    <FormFeedback>
                      {validationErrors.NumberToMultipleWorker}
                    </FormFeedback>
                  )}
                </Col>

                {booking.shiftPattern &&
                  booking.shiftPattern.map((pattern, patternIndex) => {
                    const shiftPrice = approvalForm.perShiftPrice.find(
                      (sp) => sp.shiftIndex === patternIndex
                    );
                    return (
                      <Col md={12} key={patternIndex}>
                        <Card className="border">
                          <CardHeader
                            className="bg-light"
                            style={{ cursor: "pointer" }}
                            onClick={() => togglePattern(patternIndex)}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <h6 className="mb-0">
                                Shift Pattern {patternIndex + 1} -{" "}
                                {pattern.role?.title || "N/A"}
                              </h6>
                              <i
                                className={`mdi ${
                                  openPatterns[patternIndex]
                                    ? "mdi-chevron-up"
                                    : "mdi-chevron-down"
                                }`}
                              ></i>
                            </div>
                          </CardHeader>
                          <Collapse isOpen={openPatterns[patternIndex]}>
                            <CardBody>
                              <Row className="gy-3">
                                <Col md={6}>
                                  <Label>
                                    Weekday Price{" "}
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={shiftPrice?.weekdayPrice || ""}
                                    onChange={(e) =>
                                      handlePriceChange(
                                        patternIndex,
                                        "weekdayPrice",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Enter weekday price"
                                  />
                                </Col>

                                {hasPriceType(pattern, "weekendPrice") && (
                                  <Col md={6}>
                                    <Label>Weekend Price</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={shiftPrice?.weekendPrice || ""}
                                      onChange={(e) =>
                                        handlePriceChange(
                                          patternIndex,
                                          "weekendPrice",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter weekend price"
                                    />
                                  </Col>
                                )}

                                {hasPriceType(pattern, "nightPrice") && (
                                  <Col md={6}>
                                    <Label>Night Price</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={shiftPrice?.nightPrice || ""}
                                      onChange={(e) =>
                                        handlePriceChange(
                                          patternIndex,
                                          "nightPrice",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter night price"
                                    />
                                  </Col>
                                )}

                                {hasPriceType(pattern, "urgentCoverPrice") && (
                                  <Col md={6}>
                                    <Label>Urgent Cover Price</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={shiftPrice?.urgentCoverPrice || ""}
                                      onChange={(e) =>
                                        handlePriceChange(
                                          patternIndex,
                                          "urgentCoverPrice",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter urgent cover price"
                                    />
                                  </Col>
                                )}

                                {hasPriceType(pattern, "bankHolidayPrice") && (
                                  <Col md={6}>
                                    <Label>Bank Holiday Price</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={shiftPrice?.bankHolidayPrice || ""}
                                      onChange={(e) =>
                                        handlePriceChange(
                                          patternIndex,
                                          "bankHolidayPrice",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter bank holiday price"
                                    />
                                  </Col>
                                )}
                              </Row>
                            </CardBody>
                          </Collapse>
                        </Card>
                      </Col>
                    );
                  })}

                <Col md={12}>
                  <div className="d-flex gap-2">
                    <Button
                      color="success"
                      onClick={handleApprove}
                      disabled={approveBooking.isPending}
                    >
                      {approveBooking.isPending ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Approving...
                        </>
                      ) : (
                        <>
                          <i className="mdi mdi-check me-1"></i>
                          Approve Booking
                        </>
                      )}
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      onClick={() => navigate("/booking/list")}
                    >
                      Cancel
                    </Button>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        {/* Action Buttons Row */}
        {(booking.hasSmartMatchingComplete || canGenerateInvoice()) && (
          <div className="mb-3 d-flex flex-row flex-wrap gap-2 align-items-center">
            {canGenerateInvoice() && (
              <div className="d-flex flex-wrap align-items-center gap-2 p-2 border rounded bg-light">
                <Label for="invoiceAmount" className="mb-0 fw-medium">
                  Final Amount
                </Label>
                <Input
                  id="invoiceAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(e.target.value)}
                  placeholder="Enter final amount"
                  className="w-auto"
                />
                <Button
                  color="primary"
                  onClick={handleGenerateInvoice}
                  disabled={generateInvoice.isPending}
                >
                  {generateInvoice.isPending ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="mdi mdi-file-document-outline me-1"></i>
                      Generate Invoice
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Worker assignment */}
        <WorkerAssignmentPanel booking={booking} />

        {/* Shift Patterns - Accordion View */}
        {booking.shiftPattern &&
          booking.shiftPattern.length > 0 &&
          booking.shiftPattern.map((pattern, patternIndex) => {
            const isOpen =
              openPatterns[patternIndex] !== undefined
                ? openPatterns[patternIndex]
                : false;
            return (
              <Card key={patternIndex} className="mb-3">
                <CardHeader
                  className="bg-primary text-white"
                  style={{ cursor: "pointer" }}
                  onClick={() => togglePattern(patternIndex)}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      Shift Pattern {patternIndex + 1} -{" "}
                      {pattern.role?.title || "N/A"}
                    </h5>
                    <i
                      className={`mdi text-white ${
                        isOpen ? "mdi-chevron-up" : "mdi-chevron-down"
                      }`}
                    ></i>
                  </div>
                </CardHeader>
                <Collapse isOpen={isOpen}>
                  <CardBody>
                    <Row className="gy-3 mb-3">
                      <Col md={4}>
                        <small className="text-muted d-block">Role</small>
                        <div className="fw-medium">
                          {pattern.role?.title || "-"}
                        </div>
                      </Col>
                      <Col md={4}>
                        <small className="text-muted d-block">
                          Quantity Required
                        </small>
                        <div>{pattern.quantity || "-"}</div>
                      </Col>
                      <Col md={4}>
                        <small className="text-muted d-block">Shift Time</small>
                        <div>
                          {pattern.startTime || "-"} - {pattern.endTime || "-"}
                        </div>
                      </Col>
                      <Col md={4}>
                        <small className="text-muted d-block">Break Time</small>
                        <div>{pattern.breakTime || "-"}</div>
                      </Col>
                      <Col md={4}>
                        <small className="text-muted d-block">
                          Weekday Price
                        </small>
                        <div>
                          £{parseFloat(pattern.weekdayPrice || 0).toFixed(2)}
                        </div>
                      </Col>
                      {pattern.weekendPrice && (
                        <Col md={4}>
                          <small className="text-muted d-block">
                            Weekend Price
                          </small>
                          <div>
                            £{parseFloat(pattern.weekendPrice).toFixed(2)}
                          </div>
                        </Col>
                      )}
                      <Col md={4}>
                        <small className="text-muted d-block">
                          PPE Provided
                        </small>
                        <div>
                          {pattern.hasPPEProvided ? (
                            <Badge className="bg-success">Yes</Badge>
                          ) : (
                            <Badge className="bg-secondary">No</Badge>
                          )}
                        </div>
                      </Col>
                      {pattern.ppeInstruction && (
                        <Col md={12}>
                          <small className="text-muted d-block">
                            PPE Instructions
                          </small>
                          <div>{pattern.ppeInstruction}</div>
                        </Col>
                      )}
                      {pattern.shiftDetails && (
                        <Col md={12}>
                          <small className="text-muted d-block">
                            Shift Details
                          </small>
                          <div>{pattern.shiftDetails}</div>
                        </Col>
                      )}
                    </Row>

                    {/* Supervisors */}
                    {pattern.supervisor && pattern.supervisor.length > 0 && (
                      <div className="mb-3">
                        <h6 className="mb-2">Supervisors</h6>
                        <Table responsive bordered>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Mobile</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pattern.supervisor.map((sup, idx) => (
                              <tr key={idx}>
                                <td>{sup.name || "-"}</td>
                                <td>{sup.email || "-"}</td>
                                <td>{sup.mobile || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {/* Dates Table */}
                    {pattern.dates && pattern.dates.length > 0 && (
                      <div>
                        <h6 className="mb-2">Shift Dates</h6>
                        <div className="table-responsive">
                          <Table bordered>
                            <thead className="table-light">
                              <tr>
                                <th>Date</th>
                                <th>Time</th>
                                <th>Break</th>
                                <th>Workers Required</th>
                                <th>Remaining</th>
                                <th>Assigned Workers</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pattern.dates.map((dateItem, dateIndex) => (
                                <tr key={dateIndex}>
                                  <td>
                                    {dayjs(dateItem.date).format("DD MMM YYYY")}
                                  </td>
                                  <td>
                                    {pattern.startTime || "-"} -{" "}
                                    {pattern.endTime || "-"}
                                  </td>
                                  <td>{pattern.breakTime || "-"}</td>
                                  <td>{pattern.quantity || "-"}</td>
                                  <td>
                                    <Badge
                                      className={
                                        dateItem.remaining > 0
                                          ? "bg-warning"
                                          : "bg-success"
                                      }
                                    >
                                      {dateItem.remaining || 0}
                                    </Badge>
                                  </td>
                                  <td>
                                    {dateItem.workers &&
                                    dateItem.workers.length > 0 ? (
                                      <div className="d-flex flex-column gap-2">
                                        <div className="d-flex flex-wrap gap-1">
                                          {dateItem.workers
                                            .slice(0, 3)
                                            .map((worker, wIdx) => (
                                              <Badge
                                                key={wIdx}
                                                className="bg-primary-subtle text-primary"
                                              >
                                                {worker.user?.fullName ||
                                                  worker.workerId ||
                                                  "Worker"}
                                              </Badge>
                                            ))}
                                          {dateItem.workers.length > 3 && (
                                            <Badge className="bg-light text-dark border">
                                              +{dateItem.workers.length - 3}{" "}
                                              more
                                            </Badge>
                                          )}
                                        </div>
                                        <Button
                                          size="sm"
                                          color="primary"
                                          outline
                                          className="align-self-start"
                                          onClick={() => {
                                            setSelectedDateData({
                                              ...dateItem,
                                              pattern: pattern,
                                            });
                                            setDateDetailsModal(true);
                                          }}
                                        >
                                          <i className="mdi mdi-eye me-1"></i>
                                          View Details
                                        </Button>
                                      </div>
                                    ) : (
                                      <span className="text-muted">
                                        No workers assigned
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Collapse>
              </Card>
            );
          })}

        {/* Back Button */}
        <div className="mb-3">
          <Link to="/booking/list" className="btn btn-secondary">
            <i className="mdi mdi-arrow-left me-1"></i>
            Back to Bookings List
          </Link>
        </div>

        {/* Date Details Modal */}
        <Modal
          isOpen={dateDetailsModal}
          toggle={() => setDateDetailsModal(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setDateDetailsModal(false)}>
            Date Details -{" "}
            {selectedDateData &&
              dayjs(selectedDateData.date).format("DD MMM YYYY")}
          </ModalHeader>
          <ModalBody>
            {selectedDateData && (
              <>
                {/* Date Information */}
                <Card className="mb-3">
                  <CardHeader className="bg-light">
                    <h6 className="mb-0">Date Information</h6>
                  </CardHeader>
                  <CardBody>
                    <Row className="gy-2">
                      <Col md={6}>
                        <small className="text-muted d-block">Date</small>
                        <div className="fw-medium">
                          {dayjs(selectedDateData.date).format("DD MMM YYYY")}
                        </div>
                      </Col>
                      <Col md={6}>
                        <small className="text-muted d-block">Day</small>
                        <div>{dayjs(selectedDateData.date).format("dddd")}</div>
                      </Col>
                      {selectedDateData.pattern && (
                        <>
                          <Col md={6}>
                            <small className="text-muted d-block">
                              Shift Time
                            </small>
                            <div>
                              {selectedDateData.pattern.startTime || "-"} -{" "}
                              {selectedDateData.pattern.endTime || "-"}
                            </div>
                          </Col>
                          <Col md={6}>
                            <small className="text-muted d-block">
                              Break Time
                            </small>
                            <div>
                              {selectedDateData.pattern.breakTime || "-"}
                            </div>
                          </Col>
                          <Col md={6}>
                            <small className="text-muted d-block">
                              Workers Required
                            </small>
                            <div>
                              {selectedDateData.pattern.quantity || "-"}
                            </div>
                          </Col>
                          <Col md={6}>
                            <small className="text-muted d-block">
                              Remaining
                            </small>
                            <div>
                              <Badge
                                className={
                                  selectedDateData.remaining > 0
                                    ? "bg-warning"
                                    : "bg-success"
                                }
                              >
                                {selectedDateData.remaining || 0}
                              </Badge>
                            </div>
                          </Col>
                        </>
                      )}
                    </Row>
                  </CardBody>
                </Card>

                {/* Assigned Workers */}
                {selectedDateData.workers &&
                selectedDateData.workers.length > 0 ? (
                  <Card>
                    <CardHeader className="bg-light">
                      <h6 className="mb-0">
                        Assigned Workers ({selectedDateData.workers.length})
                      </h6>
                    </CardHeader>
                    <CardBody>
                      <div className="table-responsive">
                        <Table bordered hover className="align-middle">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "35%" }}>Worker</th>
                              <th style={{ width: "35%" }}>Email</th>
                              <th>Worker ID</th>
                              <th className="text-end">Profile</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedDateData.workers.map((worker, idx) => (
                              <tr key={idx}>
                                <td className="fw-medium">
                                  {worker.user?.fullName ||
                                    worker.workerId ||
                                    "N/A"}
                                </td>
                                <td className="text-muted">
                                  {worker.user?.email || "-"}
                                </td>
                                <td>
                                  <Badge className="bg-light text-dark border">
                                    {worker.workerId || "-"}
                                  </Badge>
                                </td>
                                <td className="text-end">
                                  {worker.user?.slug ? (
                                    <Link
                                      to={`/user/user-details/worker/${worker.user.slug}`}
                                      className="btn btn-sm btn-outline-primary"
                                    >
                                      <i className="mdi mdi-eye me-1"></i>
                                      View Profile
                                    </Link>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                ) : (
                  <Card>
                    <CardBody>
                      <div className="text-center text-muted py-3">
                        <i
                          className="mdi mdi-information-outline"
                          style={{ fontSize: "48px" }}
                        ></i>
                        <p className="mt-2">No workers assigned to this date</p>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setDateDetailsModal(false)}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default BookingReview;
